import React, { useEffect, useState, useMemo } from "react";
import {
  Grid,
  MenuItem,
  TextField,
  Typography,
  Box,
  Chip,
  Popover,
  Paper,
  Button,
} from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const WEEKDAYS = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
];

const CalendarNumberPicker = ({
  value,
  onChange,
  disabled,
  selectedDayNumbers,
  startDate,
  endDate,
  index,
}: {
  value: string;
  onChange: (dayNumber: number | null) => void;
  disabled: boolean;
  selectedDayNumbers: number[];
  startDate: string;
  endDate: string;
  index: number;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);
  const selectedDayNumber = value ? new Date(value).getDate() : null;

  const getMonthsWithoutDay = (day: number) => {
    if (!startDate || !endDate || day <= 28) return [];

    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const monthsWithoutDay: string[] = [];

    let currentMonth = start.startOf('month');
    const endMonth = end.startOf('month');

    while (currentMonth.isBefore(endMonth, 'month') || currentMonth.isSame(endMonth, 'month')) {
      const daysInMonth = currentMonth.daysInMonth();
      if (day > daysInMonth) {
        monthsWithoutDay.push(currentMonth.format('MMMM YYYY'));
      }
      currentMonth = currentMonth.add(1, 'month');
    }

    return monthsWithoutDay;
  };

  const monthsWithoutSelectedDay = selectedDayNumber ? getMonthsWithoutDay(selectedDayNumber) : [];

  const handleOpen = (event: React.MouseEvent<HTMLInputElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDaySelect = (dayNumber: number) => {
    if (selectedDayNumbers.includes(dayNumber)) {
      return;
    }
    onChange(dayNumber);
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? `calendar-popover-${index}` : undefined;

  const calendarDays = Array.from({ length: 31 }, (_, i) => i + 1);

  return (
    <>
      <TextField
        fullWidth
        size="small"
        value={selectedDayNumber ? selectedDayNumber.toString() : ""}
        placeholder="Select day"
        disabled={disabled}
        onClick={handleOpen}
        inputProps={{
          readOnly: true,
        }}
        sx={{
          cursor: disabled ? "not-allowed" : "pointer",
          "& .MuiOutlinedInput-root": {
            cursor: disabled ? "not-allowed" : "pointer",
            "& input": {
              cursor: disabled ? "not-allowed" : "pointer",
              caretColor: "transparent",
            },
          },
        }}
      />
      {monthsWithoutSelectedDay.length > 0 && (
        <Box sx={{ mt: 0.5, p: 1, bgcolor: 'warning.lighter', borderRadius: 1 }}>
          <Typography variant="caption" color="warning.dark" sx={{ display: 'block', fontWeight: 500 }}>
            ⚠️ {selectedDayNumber} does not exist in: {monthsWithoutSelectedDay.join(', ')}.Task will default to the last day of those months.
          </Typography>
        </Box>
      )}

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Paper sx={{ p: 2, minWidth: 280, maxWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>
            Select Day
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {calendarDays.map((day) => {
              const isSelected = selectedDayNumber === day;
              const isDisabled = selectedDayNumbers.includes(day);
              const monthsWithoutDay = getMonthsWithoutDay(day);
              const hasWarning = monthsWithoutDay.length > 0;

              let isValidInRange = false;
              if (startDate && endDate) {
                const start = dayjs(startDate);
                const end = dayjs(endDate);
                let currentMonth = start.startOf('month');
                const endMonth = end.startOf('month');

                while (currentMonth.isBefore(endMonth, 'month') || currentMonth.isSame(endMonth, 'month')) {
                  const daysInMonth = currentMonth.daysInMonth();
                  if (day <= daysInMonth) {
                    const candidateDate = currentMonth.date(day);
                    if (candidateDate.isValid() &&
                      candidateDate.date() === day &&
                      (candidateDate.isAfter(start, 'day') || candidateDate.isSame(start, 'day')) &&
                      (candidateDate.isBefore(end, 'day') || candidateDate.isSame(end, 'day'))) {
                      isValidInRange = true;
                      break;
                    }
                  }
                  currentMonth = currentMonth.add(1, 'month');
                }
              }

              return (
                <Button
                  key={day}
                  onClick={() => !isDisabled && isValidInRange && handleDaySelect(day)}
                  disabled={isDisabled || !isValidInRange}
                  title={hasWarning && !isDisabled && isValidInRange ? `Note: ${monthsWithoutDay.join(', ')} ${monthsWithoutDay.length === 1 ? 'does' : 'do'} not have ${day} days` : ''}
                  sx={{
                    minWidth: 32,
                    height: 32,
                    p: 0,
                    fontSize: '0.875rem',
                    bgcolor: isSelected ? 'primary.main' : 'transparent',
                    color: isSelected ? 'primary.contrastText' : (hasWarning && !isDisabled && isValidInRange ? 'warning.main' : 'text.primary'),
                    border: hasWarning && !isDisabled && isValidInRange ? '1px solid' : 'none',
                    borderColor: hasWarning && !isDisabled && isValidInRange ? 'warning.main' : 'transparent',
                    '&:hover': {
                      bgcolor: isSelected ? 'primary.dark' : (isDisabled || !isValidInRange ? 'transparent' : 'action.hover'),
                    },
                    '&.Mui-disabled': {
                      opacity: isDisabled ? 0.5 : 0.3,
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  {day}
                </Button>
              );
            })}
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default function Recurrence({ value, onChange, onValidityChange, taskStartDate, disableStartDate = false, onDuplicateDayNumbersChange, onRecurrenceDatePickerOpen, closeRecurrencePickers }: any) {
  const [frequency, setFrequency] = useState<"WEEKLY" | "MONTHLY" | "">(
    value?.frequency || ""
  );
  const [interval, setInterval] = useState<number>(value?.interval || 1);
  const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>(
    value?.selectedWeekdays || []
  );
  const [monthlyDates, setMonthlyDates] = useState<string[]>(
    value?.monthlyDates || []
  );
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  // Effect to close pickers when parent requests it
  React.useEffect(() => {
    if (closeRecurrencePickers) {
      setStartOpen(false);
      setEndOpen(false);
    }
  }, [closeRecurrencePickers]);

  const startDate = disableStartDate && taskStartDate ? taskStartDate : (value?.startDate || "");
  const endDate = value?.endDate || "";

  const isWeeklyInvalid =
    frequency === "WEEKLY" && selectedWeekdays.length === 0;
  const isMonthlyInvalid =
    frequency === "MONTHLY" && monthlyDates.some((d) => !d);
  const isIntervalInvalid = frequency === "MONTHLY" && interval < 1;

  const hasDuplicateDayNumbers = useMemo(() => {
    if (frequency !== "MONTHLY") return false;
    const dayNumbers = monthlyDates
      .filter((d) => !!d)
      .map((d) => new Date(d).getDate());
    const uniqueDayNumbers = new Set(dayNumbers);
    return dayNumbers.length > uniqueDayNumbers.size;
  }, [frequency, monthlyDates]);

  const isValid =
    !!startDate &&
    !!endDate &&
    frequency &&
    !hasDuplicateDayNumbers &&
    ((frequency === "WEEKLY" && selectedWeekdays.length > 0) ||
      (frequency === "MONTHLY" && !isMonthlyInvalid && !isIntervalInvalid));

  const emitChange = (updates: any = {}) => {
    const newValue = {
      startDate,
      endDate,
      frequency,
      interval,
      selectedWeekdays,
      monthlyDates,
      ...updates,
    };
    onChange?.(newValue);
  };

  useEffect(() => {
    if (disableStartDate && taskStartDate && value?.startDate !== taskStartDate) {
      emitChange({ startDate: taskStartDate });
    }
  }, [taskStartDate, disableStartDate]);

  useEffect(() => {
    if (frequency === "MONTHLY" && value?.monthlyDates && Array.isArray(value.monthlyDates) && value.monthlyDates.length > 0) {
      const targetInterval = value.interval !== undefined && value.interval > 0 ? value.interval : value.monthlyDates.length;

      setInterval((prevInterval) => {
        if (prevInterval !== targetInterval) {
          return targetInterval;
        }
        return prevInterval;
      });

      setMonthlyDates((prevMonthlyDates) => {
        const arraysEqual = prevMonthlyDates.length === value.monthlyDates.length &&
          prevMonthlyDates.every((date, index) => date === value.monthlyDates[index]);

        if (!arraysEqual) {
          return [...value.monthlyDates];
        }
        return prevMonthlyDates;
      });
    }
  }, [value?.monthlyDates, value?.interval, value?.frequency, frequency]);

  useEffect(() => {
    if (frequency === "MONTHLY") {
      setSelectedWeekdays([]);
    }
    if (frequency !== "MONTHLY") {
      const emptyDates: string[] = [];
      setMonthlyDates(emptyDates);
      setInterval(1);
      if (frequency) {
        emitChange({ monthlyDates: emptyDates, interval: 1, selectedWeekdays: [] });
      }
      return;
    }

    setMonthlyDates((prev) => {
      if (interval > prev.length) {
        return [...prev, ...Array(interval - prev.length).fill("")];
      }
      if (interval < prev.length) {
        return prev.slice(0, interval);
      }
      return prev;
    });
  }, [frequency, interval]);

  useEffect(() => {
    if (!frequency || frequency !== "MONTHLY") return;
    if (!startDate && !endDate) return;

    const start = dayjs(startDate);
    const end = dayjs(endDate);

    setMonthlyDates((prev) =>
      prev.map((d) => {
        if (!d) return d;

        const current = dayjs(d);
        const dayNumber = current.date();

        if (current.isBefore(start, 'day')) {
          let currentMonth = start.startOf('month');
          const endMonth = end.startOf('month');

          while (currentMonth.isBefore(endMonth, 'month') || currentMonth.isSame(endMonth, 'month')) {
            const daysInMonth = currentMonth.daysInMonth();

            if (dayNumber <= daysInMonth) {
              const candidateDate = currentMonth.date(dayNumber);
              if (candidateDate.isValid() &&
                candidateDate.date() === dayNumber &&
                (candidateDate.isAfter(start, 'day') || candidateDate.isSame(start, 'day')) &&
                (candidateDate.isBefore(end, 'day') || candidateDate.isSame(end, 'day'))) {
                return candidateDate.format("YYYY-MM-DD");
              }
            }
            currentMonth = currentMonth.add(1, 'month');
          }
          return startDate;
        }

        if (current.isAfter(end, 'day')) {
          let currentMonth = end.startOf('month');
          const startMonth = start.startOf('month');

          while (currentMonth.isAfter(startMonth, 'month') || currentMonth.isSame(startMonth, 'month')) {
            const daysInMonth = currentMonth.daysInMonth();

            if (dayNumber <= daysInMonth) {
              const candidateDate = currentMonth.date(dayNumber);
              if (candidateDate.isValid() &&
                candidateDate.date() === dayNumber &&
                (candidateDate.isAfter(start, 'day') || candidateDate.isSame(start, 'day')) &&
                (candidateDate.isBefore(end, 'day') || candidateDate.isSame(end, 'day'))) {
                return candidateDate.format("YYYY-MM-DD");
              }
            }
            currentMonth = currentMonth.subtract(1, 'month');
          }
          return endDate;
        }

        return d;
      })
    );
  }, [startDate, endDate, frequency]);

  useEffect(() => {
    if (!frequency || frequency !== "MONTHLY") return;
    if (!hasDuplicateDayNumbers) return;

    const dayNumbersMap = new Map<number, number>();
    const uniqueDates: string[] = [];
    const filledDates = monthlyDates.filter(d => !!d);

    filledDates.forEach((date) => {
      const dayNumber = new Date(date).getDate();
      if (!dayNumbersMap.has(dayNumber)) {
        dayNumbersMap.set(dayNumber, uniqueDates.length);
        uniqueDates.push(date);
      }
    });

    const filledDatesCount = filledDates.length;
    if (filledDatesCount > 0 && uniqueDates.length < filledDatesCount) {
      const newInterval = Math.max(uniqueDates.length, 1);
      const updatedMonthlyDates = [...uniqueDates];

      if (newInterval !== interval || uniqueDates.length !== filledDatesCount) {
        while (updatedMonthlyDates.length < newInterval) {
          updatedMonthlyDates.push("");
        }

        if (newInterval !== interval) {
          setInterval(newInterval);
        }
        if (JSON.stringify(updatedMonthlyDates) !== JSON.stringify(monthlyDates)) {
          setMonthlyDates(updatedMonthlyDates);
          emitChange({
            interval: newInterval,
            monthlyDates: updatedMonthlyDates
          });
        }
      }
    }
  }, [hasDuplicateDayNumbers, frequency, monthlyDates, interval]);


  useEffect(() => {
    onValidityChange?.(isValid);
    onDuplicateDayNumbersChange?.(hasDuplicateDayNumbers);
  }, [isValid, hasDuplicateDayNumbers, onValidityChange, onDuplicateDayNumbersChange]);

  const handleMonthlyDateChange = (index: number, dayNumber: number | null) => {
    if (!dayNumber || !startDate || !endDate) {
      setMonthlyDates((prev) => {
        const updated = [...prev];
        updated[index] = "";
        emitChange({ monthlyDates: updated });
        return updated;
      });
      return;
    }

    const start = dayjs(startDate);
    const end = dayjs(endDate);
    let validDate: dayjs.Dayjs | null = null;

    let currentMonth = start.startOf('month');
    const endMonth = end.startOf('month');

    while (currentMonth.isBefore(endMonth, 'month') || currentMonth.isSame(endMonth, 'month')) {
      const daysInMonth = currentMonth.daysInMonth();

      if (dayNumber <= daysInMonth) {
        const candidateDate: dayjs.Dayjs = currentMonth.date(dayNumber);

        if (candidateDate.isValid() &&
          candidateDate.date() === dayNumber &&
          (candidateDate.isAfter(start, 'day') || candidateDate.isSame(start, 'day')) &&
          (candidateDate.isBefore(end, 'day') || candidateDate.isSame(end, 'day'))) {
          validDate = candidateDate;
          break;
        }
      }

      currentMonth = currentMonth.add(1, 'month');
    }

    if (validDate) {
      const dateString = validDate.format("YYYY-MM-DD");
      setMonthlyDates((prev) => {
        const updated = [...prev];
        updated[index] = dateString;
        emitChange({ monthlyDates: updated });
        return updated;
      });
    }
  };

  const getOrdinalDay = (dateString: string) => {
    if (!dateString) return "";
    const day = new Date(dateString).getDate();
    const suffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
          ? "nd"
          : day % 10 === 3 && day !== 13
            ? "rd"
            : "th";
    return `${day}${suffix}`;
  };

  const datesSelected = Boolean(startDate && endDate);

  useEffect(() => {
    if (!startDate || !endDate) return;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end < start) {
      emitChange({ endDate: "" });
    }
  }, [startDate]);

  useEffect(() => {
    if (frequency !== "MONTHLY" || !startDate || !endDate) return;

    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const daysDifference = end.diff(start, 'day') + 1;

    if (daysDifference < 31 && interval > daysDifference) {
      const newInterval = Math.max(daysDifference, 1);
      setInterval(newInterval);

      setMonthlyDates((prev) => {
        if (newInterval < prev.length) {
          const trimmed = prev.slice(0, newInterval);
          emitChange({ interval: newInterval, monthlyDates: trimmed });
          return trimmed;
        }
        return prev;
      });
    }
  }, [startDate, endDate, frequency, interval]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Typography>Start Date
          <span style={{ color: "red" }}>*</span></Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            open={startOpen}
            onOpen={() => {
              setStartOpen(true);
              setEndOpen(false);
              onRecurrenceDatePickerOpen?.();
            }}
            onClose={() => setStartOpen(false)}
            value={startDate ? dayjs(startDate) : null}
            onChange={(date) => {
              if (!disableStartDate) {
                const dateString = date ? date.format("YYYY-MM-DD") : "";
                emitChange({ startDate: dateString });
              }
            }}
            format="DD-MM-YYYY"
            disablePast
            disabled={disableStartDate}
            slotProps={{
              textField: {
                fullWidth: true,
                size: "small",
                onClick: (e: any) => {
                  if (!disableStartDate) {
                    e.stopPropagation();
                    setEndOpen(false);
                    setStartOpen(true);
                    onRecurrenceDatePickerOpen?.();
                  }
                },
                onBlur: (e: any) => {
                  if (disableStartDate) return;
                  const inputValue = e.target.value;
                  if (!inputValue) return;

                  let parsedDate = dayjs(inputValue, "DD-MM-YYYY", true);

                  if (!parsedDate.isValid()) {
                    parsedDate = dayjs(inputValue, "DD/MM/YYYY", true);
                  }
                  if (!parsedDate.isValid()) {
                    parsedDate = dayjs(inputValue, "YYYY-MM-DD", true);
                  }
                  if (!parsedDate.isValid()) {
                    parsedDate = dayjs(inputValue);
                  }

                  if (parsedDate.isValid()) {
                    const today = dayjs().startOf('day');
                    if (parsedDate.isBefore(today, 'day')) {
                      const currentDate = today.format("YYYY-MM-DD");
                      emitChange({ startDate: currentDate });
                    } else {
                      const dateString = parsedDate.format("YYYY-MM-DD");
                      emitChange({ startDate: dateString });
                    }
                  }
                },
                sx: {
                  cursor: disableStartDate ? "not-allowed" : "text",
                  "& .MuiOutlinedInput-root": {
                    cursor: disableStartDate ? "not-allowed" : "text",
                    "& input": {
                      cursor: disableStartDate ? "not-allowed" : "text",
                    },
                  },
                },
              },
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography>End Date
          <span style={{ color: "red" }}>*</span></Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            open={endOpen}
            onOpen={() => {
              setEndOpen(true);
              setStartOpen(false);
              onRecurrenceDatePickerOpen?.();
            }}
            onClose={() => setEndOpen(false)}
            value={endDate ? dayjs(endDate) : null}
            onChange={(date) => {
              const dateString = date ? date.format("YYYY-MM-DD") : "";
              emitChange({ endDate: dateString });
            }}
            format="DD-MM-YYYY"
            minDate={startDate ? dayjs(startDate) : undefined}
            slotProps={{
              textField: {
                required: true,
                fullWidth: true,
                size: "small",
                onClick: (e: any) => {
                  e.stopPropagation();
                  setStartOpen(false);
                  setEndOpen(true);
                  onRecurrenceDatePickerOpen?.();
                },
                onBlur: (e: any) => {
                  const inputValue = e.target.value;
                  if (!inputValue) return;

                  let parsedDate = dayjs(inputValue, "DD-MM-YYYY", true);

                  if (!parsedDate.isValid()) {
                    parsedDate = dayjs(inputValue, "DD/MM/YYYY", true);
                  }
                  if (!parsedDate.isValid()) {
                    parsedDate = dayjs(inputValue, "YYYY-MM-DD", true);
                  }
                  if (!parsedDate.isValid()) {
                    parsedDate = dayjs(inputValue);
                  }

                  if (parsedDate.isValid()) {
                    const today = dayjs().startOf('day');
                    const minDate = startDate ? dayjs(startDate) : today;

                    if (parsedDate.isBefore(today, 'day')) {
                      const currentDate = today.format("YYYY-MM-DD");
                      emitChange({ endDate: currentDate });
                    }
                    else if (startDate && parsedDate.isBefore(minDate, 'day')) {
                      emitChange({ endDate: startDate });
                    }
                    else {
                      const dateString = parsedDate.format("YYYY-MM-DD");
                      emitChange({ endDate: dateString });
                    }
                  }
                },
                sx: {
                  cursor: "text",
                  "& .MuiOutlinedInput-root": {
                    cursor: "text",
                    "& input": {
                      cursor: "text",
                    },
                  },
                },
              },
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography>Repeat Every
          <span style={{ color: "red" }}>*</span></Typography>
        <TextField
          select
          size="small"
          fullWidth
          value={frequency || ""}
          disabled={!datesSelected}
          onChange={(e) => {
            const newPattern = e.target.value as "WEEKLY" | "MONTHLY";
            setFrequency(newPattern);
            if (newPattern === "MONTHLY") {
              setSelectedWeekdays([]);
              setMonthlyDates(Array(interval).fill(""));
            } else {
              setMonthlyDates([]);
            }
            emitChange({
              frequency: newPattern,
              selectedWeekdays: newPattern === "WEEKLY" ? [] : [],
              monthlyDates:
                newPattern === "MONTHLY" ? Array(interval).fill("") : [],
            });
          }}
        >
          <MenuItem value="WEEKLY">Week</MenuItem>
          <MenuItem value="MONTHLY">Month</MenuItem>
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        {frequency === "WEEKLY" ? (
          <>
            <Typography>Occurs On
              <span style={{ color: "red" }}>*</span></Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {WEEKDAYS.map((day) => {
                const isSelected = selectedWeekdays.includes(day.value);

                return (
                  <Chip
                    key={day.value}
                    label={day.label.charAt(0)}
                    clickable
                    onClick={() => {
                      setSelectedWeekdays((prev) => {
                        const updated = isSelected
                          ? prev.filter((d) => d !== day.value)
                          : [...prev, day.value];
                        emitChange({ selectedWeekdays: updated });
                        return updated;
                      });
                    }}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      fontWeight: 600,
                      fontSize: 14,
                      bgcolor: isSelected ? "primary.main" : "grey.200",
                      color: isSelected
                        ? "primary.contrastText"
                        : "text.primary",
                      "&:hover": {
                        bgcolor: isSelected ? "primary.dark" : "grey.300",
                      },
                    }}
                  />
                );
              })}
            </Box>
            {isWeeklyInvalid && (
              <Typography variant="caption" color="error">
                Please select at least one weekday
              </Typography>
            )}
            {selectedWeekdays.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                {[...selectedWeekdays]
                  .sort((a, b) => a - b)
                  .map((v) => WEEKDAYS.find((d) => d.value === v)?.label)
                  .join(", ")}
              </Typography>
            )}
          </>
        ) : (
          <>
            <Typography>Repeat Times
              <span style={{ color: "red" }}>*</span></Typography>
            <TextField
              type="number"
              size="small"
              fullWidth
              inputProps={{
                min: 1,
                max: (() => {
                  if (!startDate || !endDate) return 31;
                  const start = dayjs(startDate);
                  const end = dayjs(endDate);
                  const daysDifference = end.diff(start, 'day') + 1;
                  return Math.min(daysDifference, 31);
                })()
              }}
              value={interval}
              disabled={!datesSelected}
              onChange={(e) => {
                const rawVal = e.target.value === "" ? "" : Number(e.target.value);
                if (rawVal === "") {
                  setInterval("" as any);
                } else {
                  const numVal = rawVal as number;

                  let maxInterval = 31;
                  if (startDate && endDate) {
                    const start = dayjs(startDate);
                    const end = dayjs(endDate);
                    const daysDifference = end.diff(start, 'day') + 1;
                    maxInterval = Math.min(daysDifference, 31);
                  }

                  if (numVal > maxInterval) {
                    setInterval(maxInterval);

                    let updatedMonthlyDates: string[];
                    if (maxInterval > monthlyDates.length) {
                      updatedMonthlyDates = [...monthlyDates, ...Array(maxInterval - monthlyDates.length).fill("")];
                    } else if (maxInterval < monthlyDates.length) {
                      updatedMonthlyDates = monthlyDates.slice(0, maxInterval);
                    } else {
                      updatedMonthlyDates = monthlyDates;
                    }
                    setMonthlyDates(updatedMonthlyDates);
                    emitChange({ interval: maxInterval, monthlyDates: updatedMonthlyDates });
                  } else {
                    setInterval(numVal);
                  }
                }
              }}
              onBlur={(e) => {
                const rawVal = e.target.value === "" ? 1 : Number(e.target.value);
                let val = rawVal;

                let maxInterval = 31;
                if (startDate && endDate) {
                  const start = dayjs(startDate);
                  const end = dayjs(endDate);
                  const daysDifference = end.diff(start, 'day') + 1;
                  maxInterval = Math.min(daysDifference, 31);
                }

                if (val < 1) {
                  val = 1;
                } else if (val > maxInterval) {
                  val = maxInterval;
                }

                let updatedMonthlyDates: string[];
                if (val > monthlyDates.length) {
                  updatedMonthlyDates = [...monthlyDates, ...Array(val - monthlyDates.length).fill("")];
                } else if (val < monthlyDates.length) {
                  updatedMonthlyDates = monthlyDates.slice(0, val);
                } else {
                  updatedMonthlyDates = monthlyDates;
                }

                setInterval(val);
                setMonthlyDates(updatedMonthlyDates);
                emitChange({ interval: val, monthlyDates: updatedMonthlyDates });
              }}
              error={isIntervalInvalid}
              helperText={
                isIntervalInvalid ? (() => {
                  if (!startDate || !endDate) return "Interval must be between 1 and 31";
                  const start = dayjs(startDate);
                  const end = dayjs(endDate);
                  const daysDifference = end.diff(start, 'day') + 1;
                  const maxInterval = Math.min(daysDifference, 31);
                  return `Interval must be between 1 and ${maxInterval} (based on date range)`;
                })() : ""
              }
            />
          </>
        )}
      </Grid>

      {frequency === "MONTHLY" && (
        <Grid item xs={12}>
          <Typography sx={{ mb: 1 }}>Occurs On
            <span style={{ color: "red" }}>*</span></Typography>
          <Grid container spacing={2}>
            {monthlyDates.map((date, index) => {
              const selectedDayNumbers = monthlyDates
                .map((d, i) => (d && i !== index ? new Date(d).getDate() : null))
                .filter((day): day is number => day !== null);

              return (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <CalendarNumberPicker
                    value={date}
                    onChange={(dayNumber) => handleMonthlyDateChange(index, dayNumber)}
                    disabled={!datesSelected}
                    selectedDayNumbers={selectedDayNumbers}
                    startDate={startDate}
                    endDate={endDate}
                    index={index}
                  />
                  {date && (
                    <Typography variant="caption" color="text.secondary">
                      {getOrdinalDay(date)} of every month till{" "}
                      {endDate || "end date"}
                    </Typography>
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
